import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useBandWorkspace } from "../contexts/BandWorkspaceContext";
import HeaderBar from "./HeaderBar";
import NavTabs from "./NavTabs";
import SongsTab from "./SongsTab";
import SetlistsTab from "./SetlistsTab";
import MembersTab from "./MembersTab";
import PdfViewerModal from "./PdfViewerModal";
import EditSongModal from "./EditSongModal";
import BulkUploadModal from "./BulkUploadModal";
import LiveModeViewer from "./LiveModeViewer";
import OverLimitBanner from "./OverLimitBanner";
import ConfirmModal from "./ConfirmModal";
import { SoloTrigger, BandTrigger } from "../features/upgrade/useUpgradeModal";

function SetlistModal({ open, name, date, onNameChange, onDateChange, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-sm rounded-md border border-border bg-surface p-6">
        <h2 className="mb-4 text-sm font-medium text-text-primary">Save Setlist</h2>
        <div className="mb-3 flex flex-col gap-1.5">
          <label className="text-xs uppercase tracking-wide text-text-secondary">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. Friday Night Show"
            autoFocus
          />
        </div>
        <div className="mb-5 flex flex-col gap-1.5">
          <label className="text-xs uppercase tracking-wide text-text-secondary">
            Performance Date <span className="normal-case text-text-secondary">(optional)</span>
          </label>
          <input type="date" value={date} onChange={(e) => onDateChange(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!name.trim()}
            className="border-transparent bg-accent text-white hover:bg-accent/85"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BandWorkspace() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    band,
    user,
    tenantContext,
    tier,
    activeTab,
    setActiveTab,
    showSolo,
    showBand,
    confirmModal,
    dismissConfirm,
    songs,
    workingSetlist,
    setlists,
    liveMode,
    members,
    invites,
  } = useBandWorkspace();

  const [pdfViewer, setPdfViewer] = useState(null);
  const [editModalSong, setEditModalSong] = useState(null);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const handleLogout = () => {
    signOut(auth);
    workingSetlist.setSelectedSongs([]);
    setlists.cancelSetlistModal();
    navigate("/");
  };

  return (
    <div className="App min-h-screen bg-base text-text-primary">
      <HeaderBar
        activeBandName={band.name}
        canGenerateInvite={!!band.id && user.role === "admin"}
        tenantContext={tenantContext}
        inviteCopied={invites.inviteCopied}
        isGeneratingInvite={invites.isGeneratingInvite}
        onGenerateInvite={invites.handleGenerateInvite}
        onShowUpgrade={() => showBand(BandTrigger.INVITE_MEMBER_CLICKED)}
        onLogout={handleLogout}
        error=""
      />

      <main>
        <OverLimitBanner
          tierInfo={tier}
          role={user.role}
          songCount={songs.songs.length}
          setlistCount={setlists.setlists.length}
          onUpgrade={() =>
            showSolo(
              tier.overLimitReasons?.includes("songs")
                ? SoloTrigger.SONG_LIBRARY_LIMIT_REACHED
                : SoloTrigger.SETLIST_LIMIT_REACHED
            )
          }
        />

        <NavTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {activeTab === "songs" && (
          <SongsTab
            onOpenPdfViewer={setPdfViewer}
            onOpenBulkUpload={() => setIsBulkUploadModalOpen(true)}
            onOpenEditSong={setEditModalSong}
          />
        )}
        {activeTab === "setlists" && <SetlistsTab />}
        {activeTab === "members" && (
          <MembersTab
            isDeletingAccount={isDeletingAccount}
            onSetDeletingAccount={setIsDeletingAccount}
          />
        )}

        <SetlistModal
          open={setlists.setlistModalOpen}
          name={setlists.setlistModalName}
          date={setlists.setlistModalDate}
          onNameChange={setlists.setSetlistModalName}
          onDateChange={setlists.setSetlistModalDate}
          onConfirm={setlists.handleConfirmSaveSetlist}
          onCancel={setlists.cancelSetlistModal}
        />
      </main>

      {pdfViewer && (
        <PdfViewerModal
          song={pdfViewer.song}
          resolvedUrl={pdfViewer.resolvedUrl}
          initialTab={pdfViewer.initialTab}
          onClose={() => setPdfViewer(null)}
          tenant={tenantContext}
          currentUserId={user.uid}
          currentUserName={user.displayName}
          userRole={user.role}
          onNoteCountChange={(delta) =>
            songs.patchSong(pdfViewer.song.id, { noteCount: (pdfViewer.song.noteCount || 0) + delta })
          }
        />
      )}

      {liveMode.liveMode && (
        <LiveModeViewer
          setlistName={liveMode.liveMode.setlistName}
          songs={liveMode.liveMode.songs}
          currentIndex={liveMode.liveMode.currentIndex}
          resolvedUrl={liveMode.liveMode.resolvedUrl}
          isLoadingUrl={liveMode.liveMode.isLoadingUrl}
          onNavigate={liveMode.handleLiveModeNavigate}
          onClose={liveMode.handleLiveModeClose}
          isOfflineReady={liveMode.isLiveModeOfflineReady}
          showCoachmark={new URLSearchParams(location.search).get("coachmark") === "live"}
          onCoachmarkDismiss={liveMode.handleCoachmarkDismiss}
        />
      )}

      {editModalSong && (
        <EditSongModal
          song={editModalSong}
          tenantContext={tenantContext}
          onClose={() => setEditModalSong(null)}
          onSaved={() => setEditModalSong(null)}
        />
      )}

      <BulkUploadModal
        open={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        tenantContext={tenantContext}
        onSaved={() => setIsBulkUploadModalOpen(false)}
        currentSongCount={songs.songs.length}
        songLimit={tier.songLimit}
        onLimitReached={() => showSolo(SoloTrigger.SONG_LIBRARY_LIMIT_REACHED)}
      />

      {confirmModal && (
        <ConfirmModal
          open={!!confirmModal}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          danger={confirmModal.danger}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => {
            if (!isDeletingAccount && !members.isTransferringOwnership) dismissConfirm();
          }}
          loading={isDeletingAccount || members.isTransferringOwnership}
        />
      )}
    </div>
  );
}
